export const AvatarFragments = {
  BASE_AVATAR_FOR_ADDRESS: `
  fragment BaseAvatarFields on Avatar {
      id
      version
      avatarType
      migrated
      profile {
        name
        cidV0
      }
      isBaseGroup
      verificationBadge
    }`,

  COMPLETE_AVATAR_FOR_ADDRESS: `
  fragment CompleteAvatarFields on Avatar {
      invitedBy
      lastMint
      v1Token { id }
      acceptedInviteTimestamp
      primaryGroup
      v1Stopped
      profile {
        name
        description
        location
        cidV0
      }
    }`,

  MINT_HANDLER_AVATAR_FOR_ADDRESS: `
  fragment MintHandlerAvatarFields on Avatar {
      mintHandlerAddress
      feeCollectionAddress
    }`,

  PROFILE_FOR_ADDRESS: `
  fragment AvatarFields on Avatar {
      id
      invitedBy
      cidV0
      lastMint
      version
      avatarType
      migrated
      v1Token {
        id
      }
      acceptedInviteTimestamp
      primaryGroup
      v1Stopped
      profile {
        name
        id
        description
        location
        cidV0
      }
      isBaseGroup
      mintHandlerAddress
      feeCollectionAddress
      trustsReceivedCount
      isEarlySupporter
      verificationBadge
      wrappedTokenDemurrage { id }
      wrappedTokenStatic { id }
    }`,
};

export const AvatarQueries = {
  FIND_PROFILES_BY_USERNAME: `
      query findByUserName($query: String, $avatarTypes: [avatartype!], $whitelistAddress: String, $viewerAddress: String) {
        Avatar(
          where: {
            profile: {name: {
              _ilike: $query,
              _nregex: ".*(-mint-handler|-treasury|-minter|-redeemer).*"
            }},
            avatarType: {_in: $avatarTypes}
          },
          order_by: [
            { version: desc },
            { verificationBadge: desc },
            { trustsReceivedCount: desc },
            { trustsGivenCount: desc },
            { lastMint: desc_nulls_last },
            { acceptedInviteTimestamp: desc_nulls_last },
            { profile: { name: asc } }
          ],
          limit: 50
        ) {
          id
          version
          avatarType
          isEarlySupporter
          verificationBadge
          trustsReceivedCount
          trustsGivenCount
          lastMint
          acceptedInviteTimestamp
          trustsReceived(
            where: {
              truster_id: {_eq: $whitelistAddress},
              version: {_eq: 2},
              limit: {_neq: 0},
              expiryTime: {_neq: 0}
            }
          ) {
            id
            truster_id
          }
          trustGivenToViewer: trustsGiven(where: {
            trustee_id: { _eq: $viewerAddress },
            limit: {_neq: 0},
            expiryTime: {_neq: 0},
            version: {_eq: 2}
          }) {
            trustee_id
          }
          trustReceivedFromViewer: trustsReceived(where: {
            truster_id: { _eq: $viewerAddress },
            limit: {_neq: 0},
            expiryTime: {_neq: 0},
            version: {_eq: 2}
          }) {
            truster_id
          }
          profile {
            name
            cidV0
          }
        }
      }
    `,

  PROFILE_FOR_ADDRESS: `
      ${AvatarFragments.PROFILE_FOR_ADDRESS}
      query getProfileForAddressQuery($address: String) {
        Avatar(where: {id: {_eq: $address}}) { ...AvatarFields }
      }
      `,

  PROFILE_FOR_ADDRESSES: `
      ${AvatarFragments.BASE_AVATAR_FOR_ADDRESS}
      query getProfileForAddresses($addresses: [String!]!) {
        Avatar(where: {id: {_in: $addresses}}) { ...BaseAvatarFields }
      }`,

  CIDV0_FOR_ADDRESS: `query getCidV0ForAddressQuery($address: String) {
        Avatar(where: {id: {_eq: $address}}) {
          cidV0
        }
      }
      `,
  // balance: {_gt: 100000000000000} excludes small amounts that should not be displayed!
  GET_USER_ALL_CRC_TOKENS: `
      ${AvatarFragments.BASE_AVATAR_FOR_ADDRESS}
      query getUserAllCrcTokens($address: String) {
        Avatar(where: {id: {_eq: $address}}) {
          trustsGiven(
            where: {
              limit: {_neq: 0},
              expiryTime: {_neq: 0},
              version: {_eq: 2}
            }
          ) {
            trustee_id
          }
        }
        AvatarBalance(where: {avatar_id: {_eq: $address}, balance: {_gt: 100000000000000}}) {
          id
          balance
          lastCalculated
          token {
            id
            version
            tokenType
            tokenOwner {
              ...BaseAvatarFields
              trustReceivedTokenOwner: trustsReceived(
                where: {
                  truster_id: { _eq: $address },
                  limit: {_neq: 0},
                  expiryTime: {_neq: 0},
                  version: {_eq: 2}
                }
              ) { id }
              trustGivenTokenOwner: trustsGiven(
                where: {
                  trustee_id: { _eq: $address },
                  limit: {_neq: 0},
                  expiryTime: {_neq: 0},
                  version: {_eq: 2}
                }
              ) { id }
              allTrustsReceived: trustsReceived(
                where: {
                  truster_id: { _neq: $address },
                  trustee_id: { _neq: $address },
                  limit: {_neq: 0},
                  expiryTime: {_neq: 0},
                  version: {_eq: 2}
                }
              ) {
                truster_id
              }
              trustsGiven(where: {trustee_id: {_eq: $address}, version: {_eq: 2}, limit: {_neq: 0}, expiryTime: {_neq: 0}}) {
                id
              }
            }
          }
        }
      }
    `,
  GET_OWN_CRC_TOKENS: `
    query getOwnCrcTokens($address: String!, $wrappedOnly: Boolean!) {
      AvatarBalance(
        where: {
          balance: {_gt: 0}, 
          avatar_id: {_eq: $address}, 
          token: {
            tokenOwner_id: {_eq: $address}, 
            version: {_eq: 2}, 
            tokenType: {_in: ["WrappedDemurrageToken", "WrappedStaticToken"]}
          }
        }
      ) @include(if: $wrappedOnly) {
        balance
        lastCalculated
        token {
          id
          tokenType
        }
      }
      AvatarBalance(
        where: {
          balance: {_gt: 0}, 
          avatar_id: {_eq: $address}, 
          token: {
            tokenOwner_id: {_eq: $address}, 
            version: {_eq: 2},
          }
        }
      ) @skip(if: $wrappedOnly) {
        balance
        lastCalculated
        token {
          id
          tokenType
        }
      }
    }
  `,
  // exclude really small ammounts, since the pathfinder does not process them!
  GET_AG_GROUP_CRC_BALANCE: `query getAgGroupCrcBalance($address: String, $tokenIds: [String!]!) {
      AvatarBalance(
        where: {balance: {_gt: 999999999999}, avatar_id: {_eq: $address}, token_id: {_in: $tokenIds}}
      ) {
        balance
        lastCalculated
        token { tokenType }
      }
    }`,

  ALL_USERS_WAITING_INVITES_WITH_COMPLETE_V2_PROFILES: `
    ${AvatarFragments.BASE_AVATAR_FOR_ADDRESS}
    query getProfilesAwaitingInvites($limit: Int!, $offset: Int!, $truster: String) {
      Avatar(
        where: {
          avatarType: {_in: ["Unknown", "Invite", "Migrating"]},
          profile: {name: {_is_null: false, _neq: ""}}
        }
        limit: $limit
        offset: $offset
        order_by: {blockNumber: desc}
      ) {
        ...BaseAvatarFields
        trustsReceived(where: {truster_id:{_eq:$truster}, limit:{_neq:0}, expiryTime:{_neq:0}}) {
          id
        }
      }
    }
    `,
};
